import type { BluffMove, BluffView } from "@bing/engine-bluff";
import { useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Avatar } from "../components/Avatar";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EndCardShell } from "../components/EndCardShell";
import { PromoSlot } from "../components/PromoSlot";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import { BluffTable } from "../screens/BluffTable";
import { getBotDifficulty, getCardDeck, setBotDifficulty, setCardDeck } from "../settings/prefs";
import type { BotDifficulty, CardDeckId } from "@bing/shared";
import { SoloStartScreen } from "../components/SoloStartScreen";
import { takeResumeIntent } from "./fresh-solo";
import { useSession } from "../store/context";
import { useOverlayActive } from "./lifecycle";
import { useOutcomeSfx } from "./game-sfx";
import { useEndReveal } from "./use-end-reveal";
import type { GameResult } from "./connection";
import { faDigits } from "../i18n/digits";
import { bluffSeatFallback } from "./seat-labels";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatIdentity,
  type SeatPlayer,
} from "./seat-identity";
import { createColyseusGameConnector } from "./game-connection";
import { createGameStore } from "./game-store";
import { useTableChannelGate } from "./table-channel-gate";
import { useSoloLoad } from "./use-solo-load";
import { CardDeckContext } from "./card-decks";
import { useDeckGate } from "./use-card-faces";
import { handKeyOf, useHandDeck } from "./hand-deck";

const AVATAR_IDS = ["lion", "wolf", "tiger", "eagle", "fox", "owl"] as const;
function avatarFor(seat: number): string {
  return AVATAR_IDS[seat % AVATAR_IDS.length] ?? "fox";
}

/** The design's win overlay, adapted for Bluff — an individual shedding race, so
 * seats are ranked by fewest cards left (the winner holds 0) and the player row
 * is highlighted.
 * Exported so the online Bluff game reuses the exact same end-of-match overlay. */
export function WinOverlay({
  result,
  you,
  self,
  players,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  result: GameResult;
  you: number;
  /** The signed-in user's identity — their own row wears their real photo + name. */
  self?: SeatIdentity | null;
  /** The lobby's known seat occupants; see the note on پاسور's overlay. Undefined in solo, which
   *  is exactly when the positional label is the right answer. */
  players?: readonly SeatPlayer[];
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call, so a guest sees the
   *  button inert until the host has offered one. Undefined (solo, public) ⇒ enabled. */
  againDisabled?: boolean;
  /** Why it is inert, in one Persian line. Rendered only when present. */
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: NO «بازی دوباره» at all (a dead button
   *  that looks alive is worse than none); `againHint` may still say why. Undefined (solo, and
   *  wherever the button is shown) ⇒ drawn. */
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const won = result.winners.includes(you);
  // The SAME two resolvers, and the SAME fallback, the felt uses.
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, {
      you: fa.bluff.you,
      fallback: bluffSeatFallback,
    });
  const avatarOf = (seat: number) => resolveSeatAvatar(seat, you, self, players, avatarFor);
  // score() reports 0 for an emptied hand and `-cardsLeft` otherwise, so cards
  // left = -score and the winner sorts to the top.
  const ranked = result.seatScores
    .map((score, seat) => ({ seat, cardsLeft: -score }))
    .sort((a, b) => a.cardsLeft - b.cardsLeft);
  const ranks = ["①", "②", "③", "④", "⑤", "⑥"];

  return (
    // The geometry — below the table's header band, capped, content scrolling over a fixed action
    // row, safe areas — is the shared shell's; this card keeps its own content and look.
    <EndCardShell
      label={fa.bluff.gameOver}
      scrim="rgba(0,0,0,.55)"
      cardClassName={won ? "end-card end-card--win" : "end-card end-card--lose"}
      cardStyle={{
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        borderRadius: "var(--radius-lg)",
        maxWidth: 330,
        textAlign: "center",
        boxShadow: "0 30px 70px rgba(0,0,0,.5)",
      }}
      actions={
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {againHidden ? null : (
            <Button variant="primary" onClick={onAgain} disabled={againDisabled === true}>
              {fa.bluff.playAgain}
            </Button>
          )}
          {againHint ? (
            <p style={{ margin: 0, fontSize: 13, textAlign: "center", opacity: 0.75 }}>
              {againHint}
            </p>
          ) : null}
          <Button variant="ghost" onClick={onLeave}>
            {fa.bluff.leave}
          </Button>
        </div>
      }
    >
      <div style={{ fontSize: 52, animation: "bg-float 2.4s ease-in-out infinite" }}>
        {won ? "🏆" : "🎴"}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, margin: "8px 0 18px" }}>
        {won ? fa.bluff.youWon : fa.bluff.youLost}
      </div>
      <div
        style={{
          background: "var(--surface-2)",
          borderRadius: "var(--radius)",
          padding: "6px 4px",
          marginBottom: 20,
        }}
      >
        {ranked.map((r, i) => (
          <div
            key={r.seat}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 12px",
              borderRadius: 10,
              background:
                r.seat === you ? "color-mix(in srgb,var(--accent) 14%,transparent)" : "transparent",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 800, color: "var(--accent)", width: 18 }}>
              {ranks[i]}
            </span>
            <Avatar
              avatarId={avatarOf(r.seat).avatarId}
              avatarPhotoId={avatarOf(r.seat).avatarPhotoId}
              size={26}
              official={avatarOf(r.seat).official}
            />
            {/* A long name TRUNCATES — one unbreakable Latin word used to widen the whole card off
                a 256-px screen. `dir="auto"`: an RTL block clips the BEGINNING of a Latin name;
                pinned RIGHT, not `start`, or a Latin name drifts off its face (بی‌دل's card). */}
            <span
              dir="auto"
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: "right",
                fontSize: 14,
                fontWeight: r.seat === you ? 700 : 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {nameOf(r.seat)}
            </span>
            <span style={{ fontSize: 14, fontWeight: 800 }}>{faDigits(r.cardsLeft)}</span>
          </div>
        ))}
      </div>
      {/* Post-game promo slot — renders nothing today (no campaign / flag off). At the END OF THE
          SCROLLING CONTENT, above the actions: a campaign must never push «خروج» off the card. */}
      <PromoSlot slot="POST_GAME" className="mt-3" />
    </EndCardShell>
  );
}

/** Owns the Bluff connection + store, and renders connect/error/table/game-over. */
function BluffSoloRound({
  difficulty,
  onAgain,
}: {
  /** FIXED for this game — chosen on the start screen, never changes mid-game. */
  difficulty: BotDifficulty;
  onAgain: () => void;
}): ReactNode {
  const setView = useSession((s) => s.setView);
  const initData = useSession((s) => s.initDataRaw);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [round, setRound] = useState(0);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endFailed, setEndFailed] = useState(false);

  const store = useMemo(
    () =>
      createGameStore<BluffMove, BluffView>(
        createColyseusGameConnector<BluffMove, BluffView>({
          wsUrl: GAME_SERVER_URL,
          room: "bluff",
          getInitData: () => initData,
          // FIXED for this game — chosen on the start screen, never changes mid-game.
          difficulty,
        }),
      ),
    // `round` bumps to start a fresh game (new connection) on "play again".
    [initData, round, difficulty],
  );

  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  // Hold the summary overlay for a beat so the final move/trick animates first.
  const showEnd = useEndReveal(result !== null);
  // The win/lose chime lands WITH the summary (it used to fire ~1.4s early).
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  // The app-root reconnect overlay owns the screen while it's up — don't ALSO
  // paint this container's raw-error screen behind it (that leaked English).
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      setView("lobby");
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  // The classic SVG faces are a separate chunk (see `game/card-faces.ts`): the table may not
  // be revealed until they are in, or it would deal blank cards. The bar's floor is a MINIMUM,
  // not a timeout — on a slow connection it simply waits longer.
  const deck = useContext(CardDeckContext);
  const faces = useDeckGate(deck);
  const { loading, progress, waitingForAssets } = useSoloLoad(
    view != null,
    ready,
    store,
    faces.ready,
  );
  // the deck this table DRAWS: latched per hand from the felt's first card — no hand while the
  // loading screen is up (`hand-deck.ts`, N1) — a pick lands at the next deal, never mid-hand
  const drawDeck = useHandDeck(deck, loading ? null : handKeyOf("bluff", view));

  // «خروج» — leave but KEEP the game resumable (a plain disconnect no longer ends it).
  const doLeave = (): void => {
    store.getState().disconnect();
    setView("lobby");
  };
  // «پایان بازی» — END the game server-side and WAIT for the server's ack (the
  // ActiveGame row is provably deleted) BEFORE dropping it locally and leaving. On
  // failure/timeout stay in the game, keep it listed as in-progress (it IS), and
  // surface a retry dialog — never navigate-and-forget an unconfirmed end.
  const doEnd = (): void => {
    if (ending) return;
    setEnding(true);
    setEndFailed(false);
    void store
      .getState()
      .endGame()
      .then(() => {
        clearActiveGame("bluff"); // off the resume strip right away; Lobby refetch confirms
        doLeave();
      })
      .catch(() => setEndFailed(true))
      .finally(() => setEnding(false));
  };
  // The in-game exit is gated behind the two-option confirm dialog first.
  const leave = (): void => setConfirmingLeave(true);
  // «بازی دوباره» returns to the START SCREEN rather than re-dealing at the same settings —
  // the same flow حکم/شطرنج/منچ/تخته‌نرد use, so the next game can be set up before it deals.
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.bluff.connectionError}
          </p>
          <Button variant="primary" onClick={() => setView("lobby")}>
            {fa.bluff.leave}
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !view) {
    // Retries are spent and the artwork still is not here: an explicit, recoverable screen —
    // never a silent stall, and never a table dealt with blank cards.
    if (faces.failed) {
      return <Boot error={fa.status.offline} onRetry={faces.retry} onLeave={doLeave} />;
    }
    return (
      <Boot
        caption={waitingForAssets ? fa.status.preparingCards : fa.bluff.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.bluff.leave}
      />
    );
  }

  return (
    <CardDeckContext.Provider value={drawDeck}>
      {/* The header's exit stays reachable beside the end card (the card starts below the band);
          once the game is over it is the card's «خروج» — there is no game left to keep or end. */}
      <BluffTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : leave}
      />
      {result && showEnd ? (
        // Already over (ActiveGame cleared by the terminal finish) → plain exit.
        <WinOverlay result={result} you={view.you} onAgain={again} onLeave={doLeave} />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            doEnd();
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
      {endFailed ? (
        // The server never ACKed the end — the game is still in progress and still
        // on the resume list. Same shared dialog, as the error surface: retry or stay.
        <ConfirmDialog
          title={fa.confirm.endFailedTitle}
          body={fa.confirm.endFailedBody}
          confirmLabel={fa.confirm.endRetry}
          onConfirm={doEnd}
          onCancel={() => setEndFailed(false)}
        />
      ) : null}
    </CardDeckContext.Provider>
  );
}

/**
 * Owns the START SCREEN → game flow. The opponent level and the card design are chosen here,
 * before the deal, and stay FIXED for the game; «بازی دوباره» comes back to ask again.
 *
 * A RESUME stands the screen aside ({@link takeResumeIntent}): the saved game already carries
 * the settings it began with, and the lobby arms that signal for every solo resume.
 */
export function BluffGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getBotDifficulty());
  const [deck, setDeck] = useState<CardDeckId>(getCardDeck());

  if (phase === "select") {
    return (
      <SoloStartScreen
        title={fa.bluff.title}
        emoji="🎭"
        heading={fa.soloStart.heading}
        leaveLabel={fa.bluff.leave}
        difficulty={difficulty}
        onDifficultyChange={(v) => {
          setDifficulty(v);
          setBotDifficulty(v);
        }}
        deck={deck}
        onDeckChange={(v) => {
          setDeck(v);
          setCardDeck(v);
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      />
    );
  }
  // The DECK CHOSEN ON THE START SCREEN, not the one App read when it rendered: App computes
  // `getCardDeck()` once, above this component, and never re-renders on a phase flip — so the
  // pick would not reach the table without this nested provider (the inner one wins).
  return (
    <CardDeckContext.Provider value={deck}>
      <BluffSoloRound difficulty={difficulty} onAgain={() => setPhase("select")} />
    </CardDeckContext.Provider>
  );
}
